import React from 'react';

interface BannerProps {
  url: string[] | null;
}

const AUTO_PLAY_INTERVAL_MS = 5000;

export default function Banner({ url }: BannerProps) {
  const banners = React.useMemo(
    () => (url ?? []).filter((banner): banner is string => Boolean(banner)),
    [url]
  );

  const [activeIndex, setActiveIndex] = React.useState(0);
  const [isLoaded, setIsLoaded] = React.useState(false);
  const [aspectRatio, setAspectRatio] = React.useState<number | null>(null);
  const [containerWidth, setContainerWidth] = React.useState(0);
  const containerRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    const container = containerRef.current;

    if (!container) {
      return;
    }

    const updateWidth = () => {
      setContainerWidth(container.clientWidth);
    };

    updateWidth();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateWidth);
      return () => window.removeEventListener('resize', updateWidth);
    }

    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  React.useEffect(() => {
    setActiveIndex(0);
  }, [banners]);

  React.useEffect(() => {
    setIsLoaded(false);
    setAspectRatio(null);
  }, [activeIndex, banners]);

  React.useEffect(() => {
    if (banners.length < 2) {
      return;
    }

    const timer = setInterval(() => {
      setActiveIndex((current) => (current + 1) % banners.length);
    }, AUTO_PLAY_INTERVAL_MS);

    return () => clearInterval(timer);
  }, [banners.length]);

  const isHeightCapped =
    aspectRatio !== null &&
    containerWidth > 0 &&
    containerWidth / aspectRatio > 200;

  const goToSlide = (index: number) => {
    setActiveIndex(
      ((index % banners.length) + banners.length) % banners.length
    );
  };

  if (banners.length === 0) {
    return null;
  }

  const activeBanner = banners[activeIndex];

  return (
    <div
      className="w-full md:px-[50px] py-[20px]
    "
    >
      <div
        ref={containerRef}
        className="relative flex w-full min-h-[50px] md:min-h-[100px]
        items-center justify-center overflow-hidden bg-white"
        style={{
          aspectRatio: aspectRatio ?? undefined,
          maxHeight: '400px',
        }}
      >
        {!isLoaded ? (
          <div className="absolute inset-0 animate-pulse md:!rounded-xl bg-primary-400/40" />
        ) : null}

        <img
          key={activeBanner}
          src={activeBanner}
          alt="Storefront banner"
          loading="lazy"
          decoding="async"
          onLoad={({ currentTarget }) => {
            const { naturalWidth, naturalHeight } = currentTarget;

            if (naturalWidth > 0 && naturalHeight > 0) {
              setAspectRatio(naturalWidth / naturalHeight);
            }

            setIsLoaded(true);
          }}
          onError={() => setIsLoaded(true)}
          className={`h-full w-full transition-opacity
            md:!rounded-xl duration-300 ${
              isHeightCapped ? 'object-cover' : 'object-contain'
            } ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
        />

        {banners.length > 1 ? (
          <>
            <button
              type="button"
              onClick={() => goToSlide(activeIndex - 1)}
              aria-label="Previous banner"
              className="absolute left-2 top-1/2 flex h-8 w-8 -translate-y-1/2
              items-center justify-center rounded-full bg-black/40 text-white
              transition-colors hover:bg-black/60"
            >
              ‹
            </button>
            <button
              type="button"
              onClick={() => goToSlide(activeIndex + 1)}
              aria-label="Next banner"
              className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2
              items-center justify-center rounded-full bg-black/40 text-white
              transition-colors hover:bg-black/60"
            >
              ›
            </button>

            <div className="absolute bottom-2 left-1/2 flex -translate-x-1/2 gap-1.5">
              {banners.map((banner, index) => (
                <button
                  key={banner}
                  type="button"
                  onClick={() => goToSlide(index)}
                  aria-label={`Go to banner ${index + 1}`}
                  className={`h-2 w-2 rounded-full transition-colors ${
                    index === activeIndex ? 'bg-white' : 'bg-white/50'
                  }`}
                />
              ))}
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
